import { useMemo, useState } from 'react';
import { BeltDTO, DateRangeDTO, UserPopulatedDTO } from '../../api/generated';
import { useBeltObservations } from '../../api/queries/beltObservations';
import { useBeltObservationDays } from '../../api/queries/beltObservationDays';
import BeltDayNavigator from './BeltDayNavigator';
import BeltSummary, { BeltCategoryChartConfig } from './BeltSummary';
import { Alert, Stack } from '@mui/material';
import BeltTimeline, { BeltTimelineChartConfig } from './BeltTimeline';
import { MenuBar } from '../MenuBar';
import { RESPONSIVE_DRAWER_HEIGHT } from '../../Constants';
import { Trans } from 'react-i18next';

interface BeltObservationsProps {
  user: UserPopulatedDTO;
}

export default function BeltObservations({ user }: BeltObservationsProps) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateFilter, setDateFilter] = useState<DateRangeDTO>(getStartAndEndOfDay(new Date()));
  const [monthFilter, setMonthFilter] = useState<DateRangeDTO>(getStartAndEndOfMonth(new Date()));

  const { data: beltObservations } = useBeltObservations({
    jobsiteId: user?.preferredJobsite?.id,
    dateRange: dateFilter,
  });

  const { data: beltObservationDays } = useBeltObservationDays({
    jobsiteId: user?.preferredJobsite?.id,
    dateRange: monthFilter,
  });

  const observedFirst = beltObservations?.length ? new Date(beltObservations[0].observedAt) : undefined;
  const observedLast = beltObservations?.length
    ? new Date(beltObservations[beltObservations.length - 1].observedAt)
    : undefined;

  const chartConfigs = useMemo(() => getSummaryChartConfigsByCategory(beltObservations ?? []), [beltObservations]);
  const timelineChartConfigs = useMemo(
    () => getTimelineChartConfigsByCategory(beltObservations ?? []),
    [beltObservations]
  );

  const daysWithData = useMemo(() => new Set(beltObservationDays ?? []), [beltObservationDays]);

  const handleSelectDay = (date: Date) => {
    setSelectedDate(date);
    setDateFilter(getStartAndEndOfDay(date));
  };

  const handleMonthChange = (date: Date) => {
    setMonthFilter(getStartAndEndOfMonth(date));
  };

  const supportEmail = 'support@optocycle.com';

  return (
    <Stack>
      <MenuBar />
      <Stack
        flexDirection='column'
        gap={2}
        sx={{
          position: 'absolute',
          top: RESPONSIVE_DRAWER_HEIGHT,
          width: '100%',
          height: `calc(100vh - ${RESPONSIVE_DRAWER_HEIGHT}px)`,
          padding: 2,
          boxSizing: 'border-box',
          overflow: 'auto',
        }}
      >
        <Alert severity='info'>
          <Trans i18nKey='belt.betaVersionInfo' /> <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
        </Alert>
        <BeltDayNavigator
          currentDate={selectedDate}
          observedAt={observedFirst && observedLast && { first: observedFirst, last: observedLast }}
          measurementCount={beltObservations?.length}
          daysWithData={daysWithData}
          onSelectDay={handleSelectDay}
          onMonthChange={handleMonthChange}
        />
        <BeltSummary chartConfigs={chartConfigs} />
        <BeltTimeline chartConfigs={timelineChartConfigs} />
      </Stack>
    </Stack>
  );
}

const PIE_CHART_CATEGORIES = new Set<string>(['grain-material']);

function getCategoryKeys(observations: BeltDTO[]): string[] {
  return Array.from(new Set(observations.flatMap((obs) => obs.predictions.map((p) => p.key))));
}

function getSummaryChartConfigsByCategory(observations: BeltDTO[]): BeltCategoryChartConfig[] {
  return getCategoryKeys(observations).map((category) => {
    const valueSums = new Map<string, number>();
    let observationCount = 0;

    observations.forEach((obs) => {
      const prediction = obs.predictions.find((p) => p.key === category);
      if (!prediction) return;
      observationCount += 1;
      prediction.values.forEach((v) => valueSums.set(v.key, (valueSums.get(v.key) ?? 0) + v.value));
    });

    const valueAverages = new Map(
      Array.from(valueSums, ([key, sum]) => [key, observationCount ? sum / observationCount : 0])
    );

    if (PIE_CHART_CATEGORIES.has(category)) {
      return {
        type: 'pie' as const,
        category,
        data: Array.from(valueAverages, ([label, value]) => ({ label, value })),
      };
    }

    return {
      type: 'bar' as const,
      category,
      xAxisLabels: Array.from(valueAverages.keys()),
      seriesData: Array.from(valueAverages.values()),
    };
  });
}

function getTimelineChartConfigsByCategory(observations: BeltDTO[]): BeltTimelineChartConfig[] {
  const xAxisLabels = observations.map((obs) => new Date(obs.observedAt));

  return getCategoryKeys(observations).map((category) => {
    const predictionsByObservation = observations.map((obs) => obs.predictions.find((p) => p.key === category));
    const valueKeys = new Set(predictionsByObservation.flatMap((p) => p?.values.map((v) => v.key) ?? []));

    const series = Array.from(valueKeys).map((valueKey) => ({
      key: valueKey,
      values: predictionsByObservation.map((p) => p?.values.find((v) => v.key === valueKey)?.value ?? 0),
    }));

    return { category, xAxisLabels, series };
  });
}

function getStartAndEndOfDay(date: Date): { gte: string; lt: string } {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(startOfDay);
  endOfDay.setDate(endOfDay.getDate() + 1);

  return {
    gte: startOfDay.toISOString(),
    lt: endOfDay.toISOString(),
  };
}

function getStartAndEndOfMonth(date: Date): { gte: string; lt: string } {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);

  return {
    gte: startOfMonth.toISOString(),
    lt: endOfMonth.toISOString(),
  };
}
